// src/pages/LynGame.jsx
// Piano With Pan — Learn Your Notes (LYN) Game  v2
// ─────────────────────────────────────────────────────────────────────────
// ✅ V4: recordSession called on level completion (progress tracking)
// ─────────────────────────────────────────────────────────────────────────

import React, { useRef, useState, useEffect, useMemo, useCallback, useLayoutEffect } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import confetti from "canvas-confetti";
import RealPianoListener from "../components/RealPianoListener";
import VirtualPiano, { normalizeNoteName as vpNormalizeNoteName } from "../components/VirtualPiano";
import WisPanCompanions from "../components/WisPanCompanions";
import { LEVELS, nextPlayableLevel } from "../data/lynLevels";
import { saveLevelResult } from "../utils/lynProgress";
import { recordSession } from "../utils/progressTracker"; // ✅ V4

/* ═══════════════════════════════════════════════════════════════════════
   PALETTE
═══════════════════════════════════════════════════════════════════════ */
const NOTE_COL = {
  C: { bg:"#FEE2E2", text:"#991B1B", border:"#FCA5A5", solid:"#EF4444", glow:"rgba(239,68,68,0.34)" },
  D: { bg:"#FEF3C7", text:"#92400E", border:"#FCD34D", solid:"#F59E0B", glow:"rgba(245,158,11,0.34)" },
  E: { bg:"#D1FAE5", text:"#065F46", border:"#6EE7B7", solid:"#10B981", glow:"rgba(16,185,129,0.34)" },
  F: { bg:"#DBEAFE", text:"#1E40AF", border:"#93C5FD", solid:"#3B82F6", glow:"rgba(59,130,246,0.34)" },
  G: { bg:"#EDE9FE", text:"#5B21B6", border:"#C4B5FD", solid:"#8B5CF6", glow:"rgba(139,92,246,0.34)" },
  A: { bg:"#FCE7F3", text:"#9D174D", border:"#F9A8D4", solid:"#EC4899", glow:"rgba(236,72,153,0.34)" },
  B: { bg:"#FFEDD5", text:"#9A3412", border:"#FDBA74", solid:"#F97316", glow:"rgba(249,115,22,0.34)" },
};

const LANE_ORDER = ["C","D","E","F","G","A","B"];
const NOTE_FREQ  = { C:261.63, D:293.66, E:329.63, F:349.23, G:392.0, A:440.0, B:493.88 };
const TRAVEL_MS  = 600;

const MIC_ID  = "lyn-mic-ctrl";
const MIC_CFG = {
  detectionSensitivity:1.25, toleranceCents:85, toleranceHz:14,
  wrongStableFramesRequired:1, wrongCooldownMs:180,
  wrongCentsThreshold:70, wrongHzThreshold:7,
};

/* ═══════════════════════════════════════════════════════════════════════
   FLAGS / ASSETS
═══════════════════════════════════════════════════════════════════════ */
function setTutorialDone() { try { localStorage.setItem("pwp_tutorial_done_v1","1"); } catch {} }

function buildVideoSources(filename) {
  if (!filename) return [];
  return ["/videos/"+encodeURIComponent(filename), "/videos/"+filename, "/"+filename];
}

/* ═══════════════════════════════════════════════════════════════════════
   NOTE HELPERS
═══════════════════════════════════════════════════════════════════════ */
const FLAT_TO_SHARP = { DB:"C#",EB:"D#",GB:"F#",AB:"G#",BB:"A#",CB:"B",FB:"E" };
const EDGE_ENH      = { "E#":"F","B#":"C" };
const MIDI_PC       = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];

function normSafe(n) {
  if (!n) return "";
  let r = String(n).trim().toUpperCase().replaceAll("♯","#").replaceAll("♭","B");
  const m = r.match(/[A-G](#|B)?/);
  if (!m) return "";
  let pc = m[0];
  if (EDGE_ENH[pc]) pc = EDGE_ENH[pc];
  if (pc.length===2 && pc[1]==="B") pc = FLAT_TO_SHARP[pc]||pc;
  return vpNormalizeNoteName ? vpNormalizeNoteName(pc) : pc;
}
function normAny(x) {
  if (x==null) return "";
  if (Array.isArray(x)) return normAny(x[x.length-1]??x[0]);
  if (typeof x==="number"&&Number.isFinite(x)) return normSafe(MIDI_PC[((Math.round(x)%12)+12)%12]);
  if (typeof x==="object"&&x.name) return normSafe(x.name);
  return normSafe(String(x).split(/[/,|]/)[0]);
}

function playTone(pc) {
  try {
    const freq = NOTE_FREQ[normSafe(pc)]; if (!freq) return;
    const ac  = new (window.AudioContext||window.webkitAudioContext)();
    const osc = ac.createOscillator();
    const g   = ac.createGain();
    osc.type  = "triangle";
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0.32, ac.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime+0.9);
    osc.connect(g).connect(ac.destination);
    osc.start(); osc.stop(ac.currentTime+0.95);
    osc.onended = () => ac.close().catch(()=>{});
  } catch {}
}

function wrongHint(pressedPc, targetPc) {
  const pi = LANE_ORDER.indexOf(pressedPc);
  const ti = LANE_ORDER.indexOf(targetPc);
  if (pi<0||ti<0) return { text:"Try again" };
  const dist = Math.abs(ti-pi);
  const arrow = ti<pi ? "←" : "→";
  return { text:`${arrow} ${dist} ${dist===1?"key":"keys"}` };
}

function starsFor(total, wrong) {
  if (wrong<=1) return 3;
  if (wrong<=Math.max(3, Math.ceil(total*0.35))) return 2;
  return 1;
}

/* ═══════════════════════════════════════════════════════════════════════
   MAGIC METER
═══════════════════════════════════════════════════════════════════════ */
function MagicMeter({ pct, bump }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div style={{ position:"relative", width:"100%", maxWidth:460, margin:"0 auto" }}>
      <div style={{
        width:"100%", height:34, borderRadius:999,
        background:"linear-gradient(180deg,#1e293b,#0f172a)",
        padding:3, boxShadow:"inset 0 2px 6px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.18)",
        position:"relative", overflow:"hidden",
      }}>
        <div style={{
          width:`${clamped}%`, height:"100%", borderRadius:999,
          background:"linear-gradient(180deg,#7dd3fc 0%,#38bdf8 45%,#0ea5e9 60%,#0284c7 100%)",
          boxShadow:"inset 0 2px 3px rgba(255,255,255,0.65), 0 0 14px rgba(56,189,248,0.6)",
          transition:"width .45s cubic-bezier(.34,1.56,.64,1)",
          transform: bump ? "scaleY(1.06)" : "scaleY(1)",
          position:"relative",
        }}>
          <div style={{
            position:"absolute", inset:0, borderRadius:999,
            backgroundImage:"repeating-linear-gradient(115deg, rgba(255,255,255,0.22) 0 7px, rgba(255,255,255,0) 7px 16px)",
          }}/>
        </div>
        <div style={{
          position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center",
          fontFamily:"'Fredoka One',cursive", fontSize:"0.95rem", color:"#fff",
          textShadow:"0 1px 2px rgba(0,0,0,0.55)", letterSpacing:"0.04em", pointerEvents:"none",
        }}>
          {Math.round(clamped)}%
        </div>
      </div>
      <div style={{
        position:"absolute", top:"50%", left:`${clamped}%`,
        transform:"translate(-50%,-50%)", fontSize:"1.5rem",
        transition:"left .45s cubic-bezier(.34,1.56,.64,1)",
        animation:"lynRun .5s ease-in-out infinite",
        filter:"drop-shadow(0 2px 2px rgba(0,0,0,0.35))",
        pointerEvents:"none", zIndex:5,
      }}>🐼</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   FINISH MODAL
═══════════════════════════════════════════════════════════════════════ */
function FinishModal({ open, stars, popped, total, wrong, maxCombo, onRetry, onNext, onMap }) {
  if (!open) return null;
  const accuracy = total+wrong===0 ? 100 : Math.round((total/(total+wrong))*100);
  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9999,
      background:"rgba(15,23,42,0.55)", backdropFilter:"blur(6px)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
    }}>
      <div style={{
        background:"white", borderRadius:30, padding:"30px 26px 26px",
        maxWidth:420, width:"100%",
        boxShadow:"0 28px 70px rgba(0,0,0,0.28)", textAlign:"center",
        animation:"lynPop .42s cubic-bezier(.34,1.56,.64,1)",
        maxHeight:"90dvh", overflowY:"auto",
      }}>
        <div style={{ fontSize:"2.6rem", marginBottom:2 }}>✨</div>
        <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.6rem", color:"#0f172a" }}>Magic Restored!</div>

        <div style={{ display:"flex", justifyContent:"center", gap:8, margin:"12px 0 4px" }}>
          {[0,1,2].map((i) => (
            <span key={i} style={{
              fontSize:"2.4rem", lineHeight:1,
              filter: i<stars ? "drop-shadow(0 3px 6px rgba(245,158,11,0.55))" : "grayscale(1) opacity(0.32)",
              transform: i<stars ? "scale(1)" : "scale(0.86)",
              animation: i<stars ? `lynPop .45s cubic-bezier(.34,1.56,.64,1) ${0.15*i+0.1}s both` : "none",
            }}>⭐</span>
          ))}
        </div>
        <div style={{ fontWeight:700, color:"#64748B", fontSize:"0.86rem", marginBottom:16 }}>
          {stars===3 ? "Perfect — Treble Town is glowing! 🐼🦉" : stars===2 ? "Great playing!" : "Level complete!"}
        </div>

        <div style={{ display:"flex", gap:10, justifyContent:"center", marginBottom:22 }}>
          {[
            { l:"Accuracy", v:`${accuracy}%`, c:"#16A34A", bg:"#F0FDF4" },
            { l:"Best Streak", v:`🔥${maxCombo}`, c:"#EA580C", bg:"#FFF7ED" },
            { l:"Notes", v:popped, c:"#0369A1", bg:"#F0F9FF" },
          ].map(({ l,v,c,bg }) => (
            <div key={l} style={{ flex:1, background:bg, borderRadius:16, padding:"12px 6px" }}>
              <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.3rem", color:c }}>{v}</div>
              <div style={{ fontSize:"0.55rem", fontWeight:800, textTransform:"uppercase", letterSpacing:"0.1em", color:c, opacity:0.65, marginTop:2 }}>{l}</div>
            </div>
          ))}
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {onNext && (
            <button type="button" onClick={onNext} style={{
              background:"linear-gradient(180deg,#22c55e,#16a34a)", color:"#fff",
              border:"none", borderRadius:16, padding:"13px 18px",
              fontFamily:"'Fredoka One',cursive", fontSize:"1rem", cursor:"pointer",
              boxShadow:"0 6px 16px rgba(22,163,74,0.4)", touchAction:"manipulation",
            }}>Next Level →</button>
          )}
          <button type="button" onClick={onRetry} style={{
            background:"linear-gradient(180deg,#38bdf8,#0284c7)", color:"#fff",
            border:"none", borderRadius:16, padding:"12px 18px",
            fontFamily:"'Fredoka One',cursive", fontSize:"0.98rem", cursor:"pointer",
            boxShadow:"0 6px 16px rgba(2,132,199,0.4)", touchAction:"manipulation",
          }}>Play Again ↺</button>
          <button type="button" onClick={onMap} style={{
            background:"#F1F5F9", color:"#475569", border:"none",
            borderRadius:16, padding:"11px 18px", fontWeight:800,
            fontSize:"0.9rem", cursor:"pointer", touchAction:"manipulation",
          }}>🗺️ Note Trail Map</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   MAIN GAME
═══════════════════════════════════════════════════════════════════════ */
export default function LynGame() {
  const navigate     = useNavigate();
  const { levelId: levelParam } = useParams();
  const levelId      = Number(levelParam||1);
  const level        = LEVELS[levelId];

  const fieldRef     = useRef(null);
  const pianoWrapRef = useRef(null);

  const prompts    = useMemo(() => level?.prompts||[], [level]);
  const totalNotes = useMemo(() => prompts.reduce((s,p)=>s+p.length,0), [prompts]);

  /* ── Viewport height (for dynamic field sizing on mobile) ── */
  const [vh, setVh] = useState(typeof window!=="undefined" ? window.innerHeight : 700);
  useEffect(() => {
    const fn = () => setVh(window.innerHeight);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const [lanes,  setLanes  ] = useState({});
  const [fieldH, setFieldH ] = useState(320);
  const [keyW,   setKeyW   ] = useState(52);

  const [started,  setStarted  ] = useState(false);
  const [running,  setRunning  ] = useState(false);
  const [finished, setFinished ] = useState(false);

  const [promptIndex, setPromptIndex] = useState(0);
  const [chain,       setChain      ] = useState([]);
  const [frontY,      setFrontY     ] = useState(0);
  const [popped,      setPopped     ] = useState(0);

  const [projectiles, setProjectiles] = useState([]);
  const [bursts,      setBursts     ] = useState([]);
  const [wrongBeams,  setWrongBeams ] = useState([]);
  const [bumpMeter,   setBumpMeter  ] = useState(false);

  const [combo,      setCombo     ] = useState(0);
  const [maxCombo,   setMaxCombo  ] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [stars,      setStars     ] = useState(0);

  const [micOn,     setMicOn    ] = useState(false);
  const [micLocked, setMicLocked] = useState(false);
  const [toast,     setToast    ] = useState("");

  const hasVideo    = !!(level && level.videoFile);
  const VIDEO_SRCS  = useMemo(() => buildVideoSources(level?.videoFile), [level]);
  const [showVideo,   setShowVideo  ] = useState(false);
  const [videoIdx,    setVideoIdx   ] = useState(0);
  const [videoFailed, setVideoFailed] = useState(false);

  const [panMsg,     setPanMsg    ] = useState(null);
  const [wisMsg,     setWisMsg    ] = useState(null);
  const [panReaction,setPanReaction] = useState(null);

  // ✅ V4: Session start time
  const sessionStartRef = useRef(0);

  const chainRef         = useRef(chain);
  const frontYRef        = useRef(frontY);
  const runningRef       = useRef(running);
  const phaseRef         = useRef("falling");
  const promptIndexRef   = useRef(promptIndex);
  const inputLockRef     = useRef(0);
  const lastWrongRef     = useRef({ pc:"", t:0 });
  const comboRef         = useRef(0);
  const wrongCountRef    = useRef(0);
  const idRef            = useRef(1);

  useEffect(() => { chainRef.current = chain; },         [chain]);
  useEffect(() => { frontYRef.current = frontY; },       [frontY]);
  useEffect(() => { runningRef.current = running; },     [running]);
  useEffect(() => { promptIndexRef.current = promptIndex; }, [promptIndex]);

  const FIELD_MIN = Math.min(420, Math.max(180, Math.round(vh * 0.38)));
  const GAP       = Math.max(70, Math.round(FIELD_MIN * 0.22));
  const SPAWN_TOP = 20;
  const hitLineY  = Math.max(100, FIELD_MIN - 50);
  const speedPxPerMs = (hitLineY - SPAWN_TOP) / Math.max(2000, (level?.fallSeconds||7.5)*1000);

  const measure = useCallback(() => {
    const field = fieldRef.current;
    if (!field) return;
    const fRect = field.getBoundingClientRect();
    const inv   = (field.offsetWidth ? fRect.width/field.offsetWidth : 1) || 1;
    setFieldH(field.offsetHeight||FIELD_MIN);

    const wrap   = pianoWrapRef.current;
    const availW = (wrap&&wrap.offsetWidth)||field.offsetWidth||300;
    const usable   = Math.max(140, availW - 24);
    const maxKey   = availW >= 768 ? 80 : 72;
    const nextKeyW = Math.max(36, Math.min(maxKey, Math.floor(usable / LANE_ORDER.length)));
    setKeyW(nextKeyW);

    const next = {};
    for (const pc of level?.notes ? LANE_ORDER : []) {
      let el = document.querySelector(`[data-note="${pc}4"]`)
            || document.querySelector(`[data-note="${pc}5"]`)
            || document.querySelector(`[data-note="${pc}3"]`);
      if (!el) {
        const all = pianoWrapRef.current ? pianoWrapRef.current.querySelectorAll("[data-note]") : [];
        for (const k of all) { if (normSafe(k.getAttribute("data-note"))===pc) { el=k; break; } }
      }
      if (!el) continue;
      const kRect = el.getBoundingClientRect();
      const left  = (kRect.left - fRect.left) / inv;
      const w     = kRect.width / inv;
      next[pc] = { cx: left+w/2, keyLeft:left, keyW:w };
    }
    setLanes(next);
  }, [level, FIELD_MIN]);

  useLayoutEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    const ro = typeof ResizeObserver!=="undefined" ? new ResizeObserver(measure) : null;
    if (ro && fieldRef.current)    ro.observe(fieldRef.current);
    if (ro && pianoWrapRef.current) ro.observe(pianoWrapRef.current);
    const t = setTimeout(measure, 250);
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
      clearTimeout(t);
    };
  }, [measure]);

  useEffect(() => {
    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [keyW, measure]);

  const spawnPrompt = useCallback((idx) => {
    const p = prompts[idx];
    if (!p) return;
    const newChain = p.map((pc) => ({ id:idRef.current++, pc:normSafe(pc) }));
    setChain(newChain);
    chainRef.current = newChain;
    phaseRef.current = "falling";
    setFrontY(SPAWN_TOP);
    frontYRef.current = SPAWN_TOP;
  }, [prompts]);

  const beginGame = useCallback(() => {
    if (started) return;
    // ✅ V4: Capture session start time
    sessionStartRef.current = Date.now();
    setStarted(true); setRunning(true); runningRef.current = true;
    setPanMsg(null); setWisMsg(null);
    setPromptIndex(0); promptIndexRef.current = 0;
    setPopped(0);
    comboRef.current = 0; setCombo(0); setMaxCombo(0);
    wrongCountRef.current = 0; setWrongCount(0); setStars(0);
    spawnPrompt(0);
  }, [started, spawnPrompt]);

  useEffect(() => {
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = now - last; last = now;
      if (runningRef.current && phaseRef.current==="falling") {
        const y = frontYRef.current + speedPxPerMs * dt;
        if (y >= hitLineY) {
          frontYRef.current = hitLineY; phaseRef.current = "waiting"; setFrontY(hitLineY);
        } else {
          frontYRef.current = y; setFrontY(y);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [hitLineY, speedPxPerMs]);

  const handleInput = useCallback((rawPc, source) => {
    if (!runningRef.current) return;
    const pc = normSafe(rawPc);
    if (!pc) return;
    const now = performance.now();
    if (now < inputLockRef.current) return;
    const c = chainRef.current;
    if (!c.length) return;
    const live = c[0];

    if (pc === live.pc) {
      const lane  = lanes[pc];
      const noteY = frontYRef.current;
      phaseRef.current = "waiting";
      inputLockRef.current = now + TRAVEL_MS + 60;

      if (lane) {
        const bid = idRef.current++;
        setProjectiles(prev => [...prev, { id:bid, pc, cx:lane.cx, fromY:hitLineY+30, toY:noteY }]);
        setTimeout(() => setProjectiles(prev => prev.filter(p=>p.id!==bid)), TRAVEL_MS+60);
      }

      setTimeout(() => {
        if (source!=="mic") playTone(pc);
        if (lane) {
          const burstId = idRef.current++;
          setBursts(prev => [...prev, { id:burstId, pc, cx:lane.cx, y:noteY }]);
          setTimeout(() => setBursts(prev => prev.filter(x=>x.id!==burstId)), 440);
        }
        setPopped(n => n+1);
        comboRef.current += 1; setCombo(comboRef.current);
        setMaxCombo(m => Math.max(m, comboRef.current));
        setBumpMeter(true); setTimeout(() => setBumpMeter(false), 220);

        const rest = chainRef.current.slice(1);
        chainRef.current = rest; setChain(rest);

        if (rest.length===0) {
          const nextIdx = promptIndexRef.current + 1;
          if (nextIdx >= prompts.length) {
            const earned = starsFor(totalNotes, wrongCountRef.current);
            setStars(earned);
            try { saveLevelResult(levelId, earned); } catch {}
            if (levelId===1) setTutorialDone();

            // ✅ V4: Record session for progress tracking
            try {
              recordSession({
                source:       "lyn",
                levelId,
                levelName:    level?.title || `Note Game ${levelId}`,
                totalNotes,
                correctNotes: totalNotes,
                wrongNotes:   wrongCountRef.current,
                durationMs:   sessionStartRef.current ? Date.now() - sessionStartRef.current : 0,
                stars:        earned,
              });
            } catch {}

            runningRef.current = false; setRunning(false); setFinished(true);
            setPanReaction("celebrating");
            try { confetti({ particleCount:140, spread:75, origin:{ y:0.5 } }); } catch {}
          } else {
            promptIndexRef.current = nextIdx; setPromptIndex(nextIdx);
            setTimeout(() => spawnPrompt(nextIdx), 260);
          }
        } else {
          phaseRef.current = "falling";
          frontYRef.current = hitLineY - GAP; setFrontY(hitLineY - GAP);
        }
      }, TRAVEL_MS);

    } else {
      const lw = lastWrongRef.current;
      if (lw.pc===pc && now-lw.t<320) return;
      lastWrongRef.current = { pc, t:now };
      if (!lanes[pc]) return;
      comboRef.current = 0; setCombo(0);
      wrongCountRef.current += 1; setWrongCount(cnt => cnt+1);
      const hint  = wrongHint(pc, live.pc);
      const wid   = idRef.current++;
      setWrongBeams(prev => [...prev, { id:wid, pc, hint }]);
      setTimeout(() => setWrongBeams(prev => prev.filter(b=>b.id!==wid)), 720);
    }
  }, [lanes, hitLineY, GAP, prompts.length, spawnPrompt, totalNotes, levelId, level]);

  const toggleMicButton = useCallback(() => {
    const btn = document.querySelector(`#${MIC_ID} button`);
    if (btn) btn.click();
  }, []);

  const onMicDetected = useCallback((full) => {
    if (!runningRef.current) return;
    handleInput(normAny(full), "mic");
  }, [handleInput]);

  const handlePlayNow = useCallback(() => {
    if (micLocked) { setToast("Mic is locked — press ↺ Restart to use your real piano."); return; }
    if (!started) beginGame();
    if (!micOn) { toggleMicButton(); setMicOn(true); }
  }, [micLocked, started, beginGame, micOn, toggleMicButton]);

  const handleMicToggle = useCallback(() => {
    if (micLocked) { setToast("Mic is locked — press ↺ Restart to use your real piano."); return; }
    if (!started) beginGame();
    toggleMicButton(); setMicOn(p => !p);
  }, [micLocked, started, beginGame, toggleMicButton]);

  const onVirtualKey = useCallback((name) => {
    if (!started) beginGame();
    if (micOn) { toggleMicButton(); setMicOn(false); }
    if (!micLocked) { setMicLocked(true); setToast("Switched to Virtual Piano ✅ (mic locked until ↺)"); }
    handleInput(normAny(name), "virtual");
  }, [started, beginGame, micOn, micLocked, toggleMicButton, handleInput]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => { if (!level) return; setPanMsg(level.panIntro); setWisMsg(level.wisIntro); }, [level]);

  const restart = useCallback(() => {
    if (micOn) { try { toggleMicButton(); } catch {} }
    setMicOn(false); setMicLocked(false);
    setStarted(false); setRunning(false); runningRef.current = false;
    setFinished(false);
    setPromptIndex(0); promptIndexRef.current = 0;
    setChain([]); chainRef.current = [];
    setFrontY(0); frontYRef.current = 0;
    phaseRef.current = "falling";
    setPopped(0); setProjectiles([]); setBursts([]); setWrongBeams([]);
    comboRef.current = 0; setCombo(0); setMaxCombo(0);
    wrongCountRef.current = 0; setWrongCount(0); setStars(0);
    setPanReaction(null);
    setPanMsg(level?.panIntro||null); setWisMsg(level?.wisIntro||null);
    sessionStartRef.current = 0; // ✅ V4: reset start time
  }, [micOn, toggleMicButton, level]);

  useEffect(() => () => {
    try { const btn=document.querySelector(`#${MIC_ID} button`); if(btn&&btn.textContent?.includes("Stop"))btn.click(); } catch {}
  }, []);

  useEffect(() => {
    setStarted(false); setRunning(false); runningRef.current=false; setFinished(false);
    setPromptIndex(0); promptIndexRef.current=0;
    setChain([]); chainRef.current=[];
    setFrontY(0); frontYRef.current=0; phaseRef.current="falling";
    setPopped(0); setProjectiles([]); setBursts([]); setWrongBeams([]);
    setMicOn(false); setMicLocked(false);
    comboRef.current=0; setCombo(0); setMaxCombo(0);
    wrongCountRef.current=0; setWrongCount(0); setStars(0);
    sessionStartRef.current = 0; // ✅ V4: reset on level change
    const lv = LEVELS[Number(levelId)];
    setShowVideo(!!(lv&&lv.autoVideo)); setVideoIdx(0); setVideoFailed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelId]);

  if (!level || level.comingSoon) return <Navigate to="/lyn-map" replace />;

  const magicPct      = totalNotes ? (popped/totalNotes)*100 : 0;
  const liveNote      = chain[0];
  const noteSize      = Math.max(40, Math.min(68, Math.round(keyW*0.88)));
  const whiteKeyH     = Math.max(170, Math.min(280, Math.round(keyW*3.6)));
  const blackKeyW     = Math.round(keyW*0.6);
  const blackKeyH     = Math.max(100, Math.min(175, Math.round(whiteKeyH*0.62)));

  const openVideo = () => { setVideoIdx(0); setVideoFailed(false); setShowVideo(true); };

  return (
    <div style={{
      minHeight:"100dvh",
      background:"radial-gradient(120% 120% at 50% 0%,#EEF2FF 0%,#F8FAFC 45%,#F0FDFA 100%)",
      fontFamily:"'Nunito',sans-serif",
      display:"flex", flexDirection:"column", alignItems:"center",
      overscrollBehavior:"none",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@600;700;800;900&display=swap');
        @keyframes lynPop    { from{transform:scale(.6);opacity:0} to{transform:scale(1);opacity:1} }
        @keyframes lynPulse  { 0%,100%{transform:translate(-50%,-50%) scale(1)} 50%{transform:translate(-50%,-50%) scale(1.12)} }
        @keyframes lynBeam   { 0%{opacity:.85} 100%{opacity:0} }
        @keyframes lynBurst  { 0%{transform:translate(-50%,-50%) scale(.2);opacity:1} 100%{transform:translate(-50%,-50%) scale(2.1);opacity:0} }
        @keyframes lynGlow   { 0%,100%{opacity:.7} 50%{opacity:1} }
        @keyframes lynBullet { from{transform:translate(-50%,0)} to{transform:translate(-50%,var(--ty))} }
        @keyframes lynRun    { 0%,100%{transform:translate(-50%,-50%) rotate(-5deg)} 50%{transform:translate(-50%,-64%) rotate(5deg)} }
        button { -webkit-tap-highlight-color:transparent; touch-action:manipulation; }
      `}</style>

      <div style={{ width:"100%", display:"flex", flexDirection:"column", alignItems:"center", padding:"12px 10px 16px", flex:1, maxWidth:"100%" }}>

        {/* Header */}
        <div style={{
          width:"100%", maxWidth:900,
          display:"flex", alignItems:"center", justifyContent:"space-between",
          marginBottom:8, gap:6,
        }}>
          <button type="button" onClick={() => navigate(-1)} style={{
            background:"rgba(255,255,255,0.85)", border:"1px solid #E2E8F0",
            borderRadius:12, padding:"8px 12px", fontWeight:800,
            color:"#475569", cursor:"pointer", fontSize:"0.82rem",
            flexShrink:0, touchAction:"manipulation",
          }}>← Back</button>

          <div style={{
            fontFamily:"'Fredoka One',cursive", color:"#334155",
            fontSize:"clamp(0.8rem,2.2vw,1rem)", textAlign:"center",
            flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
            padding:"0 4px",
          }}>
            {level.title}
          </div>

          <div style={{ display:"flex", gap:5, flexShrink:0 }}>
            {hasVideo && (
              <button type="button" onClick={openVideo} style={{
                background:"rgba(255,255,255,0.85)", border:"1px solid #E2E8F0",
                borderRadius:12, padding:"8px 10px", fontWeight:800,
                fontSize:"0.72rem", color:"#475569", cursor:"pointer", whiteSpace:"nowrap",
              }}>▶ Video</button>
            )}
            <button type="button" onClick={handleMicToggle} disabled={micLocked} style={{
              background: micLocked?"#FEE2E2": micOn?"#DCFCE7":"rgba(255,255,255,0.85)",
              border:`1px solid ${micLocked?"#FCA5A5":micOn?"#86EFAC":"#E2E8F0"}`,
              borderRadius:12, padding:"8px 9px", fontWeight:800, fontSize:"0.72rem",
              color: micLocked?"#B91C1C": micOn?"#15803D":"#475569",
              cursor: micLocked?"not-allowed":"pointer", whiteSpace:"nowrap",
            }}>
              {micLocked ? "🎤 LOCKED" : micOn ? "🎤 ON" : "🎤 OFF"}
            </button>
            <button type="button" onClick={restart} style={{
              background:"rgba(255,255,255,0.85)", border:"1px solid #E2E8F0",
              borderRadius:12, padding:"8px 9px", fontWeight:800,
              fontSize:"0.78rem", color:"#475569", cursor:"pointer",
            }}>↺</button>
          </div>
        </div>

        {/* Card */}
        <div style={{
          width:"100%", maxWidth:900, flex:1,
          background:"rgba(255,255,255,0.96)", borderRadius:24,
          boxShadow:"0 20px 50px rgba(15,23,42,0.10), 0 2px 0 rgba(255,255,255,0.6) inset",
          border:"1px solid rgba(226,232,240,0.9)",
          padding:"14px 14px 12px",
          display:"flex", flexDirection:"column",
          position:"relative", overflow:"hidden",
        }}>
          <MagicMeter pct={magicPct} bump={bumpMeter} />

          {/* Falling field */}
          <div ref={fieldRef} style={{
            position:"relative", flex:1,
            minHeight: FIELD_MIN,
            marginTop:10, borderRadius:14, overflow:"hidden",
          }}>
            {/* Lane guides */}
            {LANE_ORDER.map((pc) => {
              const lane    = lanes[pc]; if (!lane) return null;
              const isActive = liveNote && liveNote.pc===pc;
              const col     = NOTE_COL[pc];
              return (
                <div key={`lane-${pc}`}>
                  <div style={{ position:"absolute", left:lane.keyLeft+lane.keyW-0.5, top:0, bottom:0, width:1, background:"rgba(148,163,184,0.18)" }}/>
                  {isActive && <div style={{ position:"absolute", left:lane.keyLeft+2, width:lane.keyW-4, top:0, bottom:0, borderRadius:10, background:`linear-gradient(180deg,${col.bg}00 0%,${col.bg} 100%)`, opacity:0.5 }}/>}
                </div>
              );
            })}

            {/* Combo badge */}
            {combo>=2 && (
              <div key={combo} style={{
                position:"absolute", top:8, right:10, zIndex:20,
                display:"flex", alignItems:"center", gap:5, padding:"5px 11px",
                borderRadius:999,
                background: combo>=8 ? "linear-gradient(180deg,#f472b6,#db2777)"
                          : combo>=5 ? "linear-gradient(180deg,#fbbf24,#d97706)"
                          :            "linear-gradient(180deg,#fb923c,#ea580c)",
                color:"#fff", fontFamily:"'Fredoka One',cursive", fontSize:"0.85rem",
                boxShadow:"0 4px 14px rgba(234,88,12,0.4)",
                animation:"lynPop .3s cubic-bezier(.34,1.56,.64,1)", pointerEvents:"none",
              }}>🔥 {combo} streak</div>
            )}

            {/* Hit line */}
            <div style={{ position:"absolute", left:6, right:6, top:hitLineY, height:2, borderRadius:2, background:"repeating-linear-gradient(90deg,#cbd5e1 0 8px,transparent 8px 16px)", opacity:0.7 }}/>

            {/* Wrong beams */}
            {wrongBeams.map((b) => {
              const lane = lanes[b.pc]; if (!lane) return null;
              return (
                <div key={b.id} style={{
                  position:"absolute", left:lane.cx, top:0, bottom:0, width:5,
                  transform:"translateX(-50%)",
                  background:"linear-gradient(180deg,#ef4444,#b91c1c)",
                  boxShadow:"0 0 16px rgba(239,68,68,0.8)",
                  borderRadius:3, animation:"lynBeam .72s ease-out forwards", zIndex:14,
                }}>
                  <div style={{
                    position:"absolute", top:Math.max(6,hitLineY-38), left:"50%",
                    transform:"translateX(-50%)", whiteSpace:"nowrap",
                    background:"#b91c1c", color:"#fff",
                    fontFamily:"'Fredoka One',cursive", fontSize:"0.7rem",
                    padding:"2px 7px", borderRadius:999,
                  }}>{b.hint.text}</div>
                </div>
              );
            })}

            {/* Laser bullets */}
            {projectiles.map((p) => {
              const col = NOTE_COL[p.pc]||NOTE_COL.C;
              return (
                <div key={p.id} style={{
                  position:"absolute", left:p.cx, top:p.fromY,
                  "--ty":`${p.toY-p.fromY}px`, transform:"translate(-50%,0)",
                  zIndex:13, animation:`lynBullet ${TRAVEL_MS}ms linear forwards`, pointerEvents:"none",
                }}>
                  <div style={{ position:"absolute", left:"50%", top:4, width:4, height:24, transform:"translateX(-50%)", borderRadius:3, background:`linear-gradient(180deg,${col.solid},transparent)`, opacity:0.7 }}/>
                  <div style={{ width:16, height:16, borderRadius:"50%", transform:"translate(-50%,-50%)", background:`radial-gradient(circle at 50% 38%,#fff 0%,${col.solid} 55%,${col.border} 100%)`, boxShadow:`0 0 9px ${col.solid},0 0 18px ${col.glow}` }}/>
                </div>
              );
            })}

            {/* Bursts */}
            {bursts.map((bu) => {
              const col = NOTE_COL[bu.pc]||NOTE_COL.C;
              return <div key={bu.id} style={{ position:"absolute", left:bu.cx, top:bu.y, width:60, height:60, borderRadius:"50%", background:`radial-gradient(circle,${col.bg} 0%,${col.glow} 55%,transparent 70%)`, animation:"lynBurst .44s ease-out forwards", zIndex:12 }}/>;
            })}

            {/* Falling notes */}
            {chain.map((n, slot) => {
              const lane = lanes[n.pc]; if (!lane) return null;
              const y    = frontY - slot * GAP;
              if (y < -50 || y > fieldH+50) return null;
              const col     = NOTE_COL[n.pc]||NOTE_COL.C;
              const isLive  = slot===0;
              const waiting = isLive && phaseRef.current==="waiting";
              return (
                <div key={n.id} style={{
                  position:"absolute", left:lane.cx, top:y,
                  transform:"translate(-50%,-50%)",
                  width:noteSize, height:noteSize,
                  borderRadius:Math.round(noteSize*0.3),
                  background:`linear-gradient(180deg,#fff 0%,${col.bg} 100%)`,
                  border:`3px solid ${col.solid}`,
                  boxShadow:`0 7px 18px ${col.glow},inset 0 2px 4px rgba(255,255,255,0.8)`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontFamily:"'Fredoka One',cursive",
                  fontSize:`${Math.max(1.1, noteSize/34)}rem`,
                  color:col.text, opacity:isLive?1:0.82,
                  zIndex:isLive?11:10-slot,
                  animation:waiting?"lynPulse 0.9s ease-in-out infinite":"none",
                }}>{n.pc}</div>
              );
            })}

            {/* Pre-game overlay */}
            {!started && !showVideo && (
              <div style={{
                position:"absolute", inset:0,
                background:"rgba(248,250,252,0.84)", backdropFilter:"blur(3px)",
                display:"flex", flexDirection:"column", alignItems:"center",
                justifyContent:"center", gap:12, zIndex:50, borderRadius:14,
              }}>
                <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"clamp(1.1rem,4vw,1.5rem)", color:"#0f172a", textAlign:"center", padding:"0 16px" }}>
                  Collect the magic — learn your notes! ✨
                </div>
                <div style={{ color:"#64748B", fontWeight:700, fontSize:"0.86rem", textAlign:"center", maxWidth:360 }}>
                  Press <b>Play Now</b> to use your real piano, or just tap the keys below.
                </div>
                <button type="button" onClick={handlePlayNow} style={{
                  background:"linear-gradient(180deg,#22c55e,#16a34a)", color:"#fff",
                  border:"none", borderRadius:999, padding:"13px 30px",
                  fontFamily:"'Fredoka One',cursive", fontSize:"1.1rem", cursor:"pointer",
                  boxShadow:"0 8px 22px rgba(22,163,74,0.4)",
                }}>🎤 Play Now</button>
                {hasVideo && (
                  <button type="button" onClick={openVideo} style={{
                    background:"rgba(255,255,255,0.9)", color:"#0369A1",
                    border:"2px solid #bae6fd", borderRadius:999, padding:"8px 18px",
                    fontWeight:800, fontSize:"0.82rem", cursor:"pointer",
                  }}>▶ Watch: how to find your notes</button>
                )}
              </div>
            )}
          </div>

          {/* Piano */}
          <div ref={pianoWrapRef} style={{ position:"relative", marginTop:8, display:"flex", justifyContent:"center" }}>
            {liveNote && lanes[liveNote.pc] && (
              <div style={{
                position:"absolute", left:lanes[liveNote.pc].keyLeft, width:lanes[liveNote.pc].keyW,
                top:0, bottom:0, borderRadius:10,
                border:`3px solid ${NOTE_COL[liveNote.pc].solid}`,
                boxShadow:`0 0 16px ${NOTE_COL[liveNote.pc].glow},inset 0 0 12px ${NOTE_COL[liveNote.pc].glow}`,
                pointerEvents:"none", zIndex:30,
                animation:"lynGlow 1.1s ease-in-out infinite",
              }}/>
            )}
            <VirtualPiano
              firstKey={level.firstKey} lastKey={level.lastKey}
              onNoteClick={onVirtualKey}
              keyWidth={keyW} whiteKeyHeight={whiteKeyH}
              blackKeyWidth={blackKeyW} blackKeyHeight={blackKeyH}
            />
          </div>

          {/* Toast */}
          {toast && (
            <div style={{
              position:"absolute", bottom:12, left:"50%", transform:"translateX(-50%)",
              background:"#0f172a", color:"#fff", padding:"8px 14px", borderRadius:999,
              fontWeight:700, fontSize:"0.78rem", boxShadow:"0 6px 18px rgba(0,0,0,0.25)",
              zIndex:60, maxWidth:"88%", textAlign:"center",
            }}>{toast}</div>
          )}
        </div>
      </div>

      {/* Hidden mic listener */}
      <div id={MIC_ID} style={{ position:"absolute", width:0, height:0, overflow:"hidden", opacity:0, pointerEvents:"none" }} aria-hidden="true">
        <RealPianoListener
          targetMelody={[]} onNoteDetected={onMicDetected}
          showMicHint={false} startLabel="Play Now" stopLabel="Stop"
          {...MIC_CFG}
        />
      </div>

      {/* Companions */}
      <WisPanCompanions
        panMessage={panMsg} wisMessage={wisMsg}
        autoDismissPan={started} autoDismissWis={started}
        dismissAfterMs={4000} panReaction={panReaction}
      />

      {/* ✅ Note Trail Map FAB */}
      <button
        type="button"
        onClick={() => navigate("/lyn-map")}
        aria-label="Note Trail Map"
        style={{
          position:"fixed", right:14,
          bottom:`calc(20px + env(safe-area-inset-bottom, 0px))`,
          zIndex:90,
          display:"flex", flexDirection:"column", alignItems:"center", gap:2,
          padding:"9px 11px", borderRadius:16,
          background:"linear-gradient(135deg,#cffafe,#ffffff)",
          border:"2px solid #22d3ee", cursor:"pointer",
          boxShadow:"0 4px 0 #0891b2,0 7px 16px rgba(8,145,178,0.25)",
          transition:"all 0.2s", minWidth:68,
          touchAction:"manipulation",
        }}
        onMouseEnter={e => e.currentTarget.style.transform="translateY(-2px)"}
        onMouseLeave={e => e.currentTarget.style.transform="translateY(0)"}
      >
        <span style={{ fontSize:"1.5rem", lineHeight:1 }}>🗺️</span>
        <span style={{ fontFamily:"'Fredoka One',cursive", fontSize:"0.68rem", color:"#0e7490", letterSpacing:"0.02em" }}>Map</span>
      </button>

      {/* Video overlay */}
      {showVideo && (
        <div style={{ position:"fixed", inset:0, zIndex:10000, background:"rgba(0,0,0,0.8)", backdropFilter:"blur(8px)", display:"flex", alignItems:"center", justifyContent:"center", padding:14 }}>
          <div style={{ background:"#fff", borderRadius:22, padding:16, maxWidth:700, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.4)", position:"relative", maxHeight:"94dvh", overflowY:"auto" }}>
            <button type="button" onClick={() => setShowVideo(false)} style={{ position:"absolute", top:10, right:12, background:"none", border:"none", fontSize:"1.3rem", color:"#94a3b8", cursor:"pointer" }}>✕</button>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.1rem", color:"#1e293b", textAlign:"center", marginBottom:10 }}>🎹 How to find your notes</div>
            {!videoFailed ? (
              <div style={{ width:"100%", aspectRatio:"16/9", background:"#000", borderRadius:12, overflow:"hidden" }}>
                <video
                  src={VIDEO_SRCS[videoIdx]} controls autoPlay playsInline
                  style={{ width:"100%", height:"100%", objectFit:"contain", background:"#000", display:"block" }}
                  onEnded={() => setShowVideo(false)}
                  onError={() => setVideoIdx(i => { if(i+1<VIDEO_SRCS.length) return i+1; setVideoFailed(true); return i; })}
                />
              </div>
            ) : (
              <div style={{ padding:"28px 16px", textAlign:"center", color:"#64748b", fontWeight:700, fontSize:"0.86rem" }}>
                Video not found. Place it at <code>/public/videos/{level.videoFile}</code>.
              </div>
            )}
            <div style={{ display:"flex", justifyContent:"center", marginTop:12 }}>
              <button type="button" onClick={() => setShowVideo(false)} style={{ padding:"10px 22px", borderRadius:999, background:"linear-gradient(180deg,#22c55e,#16a34a)", color:"#fff", border:"none", cursor:"pointer", fontFamily:"'Fredoka One',cursive", fontSize:"0.92rem", boxShadow:"0 5px 14px rgba(22,163,74,0.4)" }}>Let's Play! →</button>
            </div>
          </div>
        </div>
      )}

      {/* Finish modal */}
      <FinishModal
        open={finished} stars={stars} popped={popped} total={totalNotes}
        wrong={wrongCount} maxCombo={maxCombo}
        onRetry={restart}
        onNext={nextPlayableLevel(levelId) ? () => { restart(); navigate(`/lyn-game/${nextPlayableLevel(levelId)}`); } : null}
        onMap={() => navigate("/lyn-map")}
      />
    </div>
  );
}